import { EDITABLE_KNOWLEDGE_FIELD, isKnowledgeEditableTrait } from '@shared';
import * as fromGenerated from '../../_generated';
import { TypeOptions } from './assistant-builder/assistant-builder-type-selector.component';
import type { BuilderRouteMode } from './store/assistants.reducer';

export type AssistantsPageTab = 'createdByMe' | 'sharedWithMe';

/** Which pencil, if any, a viewer gets on a skill card (SB-3650 / KD-2). */
export type SkillCardEditCapability = 'builder' | 'knowledge' | 'none';

export type SkillCardEditAffordance =
  | {
      capability: 'builder';
      /** The i18n key for the pencil's tooltip. Always set — 'none' still binds a
       *  required input, and the `@if` hides the button. */
      tooltipKey: string;
      builderRoute: BuilderRouteMode;
    }
  | {
      capability: 'knowledge' | 'none';
      /** The i18n key for the pencil's tooltip. Always set — 'none' still binds a
       *  required input, and the `@if` hides the button. */
      tooltipKey: string;
    };

/** The owner's pencil, and the fallback the hidden button still binds. */
const SKILL_BUILDER_TOOLTIP_KEY = 'ASSISTANTS.DROPDOWN_MENU.EDIT';

/**
 * The traits the builder can show; `research`, `wizard` and `extract` are not among them.
 * An own-key check, not `in`: `in` also walks the prototype chain, so a runtime value such as `constructor`
 * would pass as a builder trait.
 */
export function isBuilderTrait(
  trait: fromGenerated.AssistantView.TraitEnum,
): boolean {
  return Object.hasOwn(TypeOptions, trait);
}

/**
 * Whether a skill of this trait can be the default chat (SB-3944 KD-51): Flex
 * (`generic`) only. The API refuses every other trait with a 400, so the admin
 * list does not offer it. A Flex skill whose message box is disabled
 * (`removePromptInput`) is also refused by the API; the list row carries no
 * `traitConfig`, so that case answers with the API's error toast.
 */
export function canBeDefaultChat(
  trait: fromGenerated.AssistantView.TraitEnum,
): boolean {
  return trait === 'generic';
}

/**
 * Which pencil, if any, a viewer gets on a skill card, and what it says
 * (SB-3650 / KD-2, SB-3650 / KD-17).
 *
 * Three-valued, not two booleans: the owner gets the builder and the Edit
 * knowledge modal never opens on a skill you own (SB-3650 / KD-19), so "both"
 * is a state the product forbids and the type should not admit. The one owner
 * without a pencil holds a reserved trait (`research`, `wizard`, `extract`):
 * the builder has no form for it and the API refuses every PATCH on it. The
 * tooltip travels with the verdict because it is keyed on capability AND trait.
 *
 * This is an AFFORDANCE, never the control. Role and trait are mirrored exactly
 * by server-side assertions (`assistants.validators.ts`). The other two
 * conjuncts are the browser NARROWING on its own: the server accepts an owner's
 * PUT, and its floor is post-state (SB-3650 / KD-15), so it does not require a
 * pre-existing editable document. Do not relax either on the belief that the
 * server re-checks it — for those two it does not.
 *
 * PHASE 2 rewrites this. Per-skill grants retire the role gate, so the third
 * parameter becomes a per-skill capability and the fourth a per-skill route
 * flag; the type, the tooltip descriptor and the i18n keys are trait-bound and
 * stay. The compiler will not catch a swap of the third and fourth booleans,
 * which is why the spec pins both.
 */
export function resolveSkillCardEditCapability(
  assistant: Pick<
    fromGenerated.AssistantView,
    | 'ownedBy'
    | 'trait'
    | 'hasEditableKnowledgeDocuments'
    | 'isManagedBySquadBox'
  >,
  userId: string | undefined,
  canManageKnowledgeByRole: boolean,
  isSuperAdmin: boolean,
): SkillCardEditAffordance {
  const none: SkillCardEditAffordance = {
    capability: 'none',
    tooltipKey: SKILL_BUILDER_TOOLTIP_KEY,
  };

  // SB-3650. Unreachable in the app: `/chats` is behind `authGuard`
  // (`app.routes.ts:34`), which only activates once `selectUser` is populated,
  // and BOTH this id and `canManageKnowledgeByRole` derive from that same
  // selector — so they are never in disagreement, on either surface. Guarded
  // anyway, because the parameter is declared optional and an unknown viewer
  // cannot be SHOWN to be a non-owner; falling through would answer
  // `'knowledge'`, which asserts exactly that.
  if (!userId) {
    return none;
  }
  if (assistant.ownedBy === userId) {
    // A reserved trait opens no builder (it would crash on the missing type
    // option) and never the knowledge modal either (SB-3650 / KD-19).
    if (!isBuilderTrait(assistant.trait)) {
      return none;
    }
    return {
      capability: 'builder',
      tooltipKey: SKILL_BUILDER_TOOLTIP_KEY,
      builderRoute: 'owner',
    };
  }

  if (
    isSuperAdmin &&
    assistant.isManagedBySquadBox &&
    isBuilderTrait(assistant.trait)
  ) {
    return {
      capability: 'builder',
      tooltipKey: SKILL_BUILDER_TOOLTIP_KEY,
      builderRoute: 'admin',
    };
  }

  if (!canManageKnowledgeByRole || !assistant.hasEditableKnowledgeDocuments) {
    return none;
  }
  if (!isKnowledgeEditableTrait(assistant.trait)) {
    return none;
  }

  return {
    capability: 'knowledge',
    tooltipKey: EDITABLE_KNOWLEDGE_FIELD[assistant.trait].tooltipKey,
  };
}

/**
 * Splits the skills My Skills lists into its two tabs. `listedAssistants`
 * must come from `selectAllListed`: every non-owned skill in
 * it is then one the access graph gives the user, that is, shared with them.
 * The raw list would put a skill the user reads only by role under "Shared
 * with me".
 */
export function filterAssistantsByTabs(
  listedAssistants: fromGenerated.AssistantView[],
  userId: string | undefined,
): Record<AssistantsPageTab, fromGenerated.AssistantView[]> {
  const assistantsCreatedByUser: fromGenerated.AssistantView[] = [];
  const assistantsSharedWithUser: fromGenerated.AssistantView[] = [];

  for (const assistant of listedAssistants) {
    if (userId && assistant.ownedBy === userId) {
      assistantsCreatedByUser.push(assistant);
    } else {
      // Shared with them (see above).
      assistantsSharedWithUser.push(assistant);
    }
  }

  return {
    createdByMe: assistantsCreatedByUser,
    sharedWithMe: assistantsSharedWithUser,
  };
}

export const convertLangToAssistantLocale = (
  lang: string | undefined,
): fromGenerated.AssistantTranslationView.LocaleEnum => {
  if (lang === 'en' || lang === 'fr') {
    return lang;
  }
  return 'und';
};
